import { useEffect, useMemo, useState } from 'react';
import { ArrowUturnLeftIcon, SwatchIcon } from '@heroicons/react/24/outline';
import { Button, Card, Input, LoadingSpinner, Badge } from '@/components/common';
import { cn } from '@/utils';
import {
  type MicrositeDevice,
  type MicrositePageType,
  type ThemeFieldSpec,
  type ThemeTokens,
} from '@/services/microsite';
import {
  useMicrositeLayout,
  useMicrositePreview,
  useMicrositeTheme,
  useResetMicrositeTheme,
  useSaveMicrositeTheme,
} from '@/hooks/useMicrosite';
import { PreviewPane } from './PreviewPane';
import { humanize } from './sectionUtils';

function specType(spec: ThemeFieldSpec): string {
  return typeof spec === 'string' ? spec : (spec.type ?? 'str');
}

function specChoices(spec: ThemeFieldSpec): string[] | undefined {
  return typeof spec === 'string' ? undefined : spec.choices;
}

/** Placeholder hints for the free-text length fields, so "24px" is discoverable. */
const LENGTH_PLACEHOLDERS: Record<string, string> = {
  base_size: '16px',
  content_width: '1120px',
  section_padding: '56px',
  grid_gap: '20px',
  card_padding: '22px',
  radius: '12px',
};

/**
 * The theme editor.
 *
 * Every control is generated from the `fields` schema the server returns, not
 * from a hard-coded list here. `apps/microsite/theme.py` is the only place that
 * knows which tokens exist and what CSS they compile to; a second copy in this
 * file would drift the moment someone adds a dial, and the drift would show up
 * as a control that silently does nothing.
 */
export function ThemePanel({
  editable = true,
  previewPageType = 'landing',
}: {
  editable?: boolean;
  previewPageType?: MicrositePageType;
}) {
  const { data: theme, isLoading } = useMicrositeTheme();
  const save = useSaveMicrositeTheme();
  const reset = useResetMicrositeTheme();

  const [draft, setDraft] = useState<ThemeTokens>({});
  const [isDirty, setIsDirty] = useState(false);
  const [device, setDevice] = useState<MicrositeDevice>('desktop');

  // Preview the saved layout with the *draft* theme, so colour changes are
  // visible before they are committed.
  const { data: layout } = useMicrositeLayout(previewPageType);
  const preview = useMicrositePreview({
    pageType: previewPageType,
    sections: useMemo(
      () => [...(layout?.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      [layout],
    ),
    device,
    theme: draft,
    enabled: !!theme,
  });

  useEffect(() => {
    if (theme && !isDirty) setDraft(theme.tokens ?? {});
  }, [theme, isDirty]);

  if (isLoading || !theme) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const setToken = (group: string, field: string, value: unknown) => {
    setDraft((prev) => {
      const nextGroup = { ...(prev[group] ?? {}) };
      if (value === undefined || value === '') {
        delete nextGroup[field];
      } else {
        nextGroup[field] = value;
      }
      const next = { ...prev, [group]: nextGroup };
      // Drop an emptied group so an untouched theme stays literally `{}` — and
      // an empty theme compiles to no CSS at all, i.e. the stock look.
      if (Object.keys(nextGroup).length === 0) delete next[group];
      return next;
    });
    setIsDirty(true);
  };

  return (
    <div className="grid gap-6 pb-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <SwatchIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-base font-semibold">Theme</h3>
                {theme.is_base ? (
                  <Badge variant="warning" size="sm">
                    Platform default
                  </Badge>
                ) : theme.is_customized ? (
                  <Badge variant="success" size="sm">
                    Customized
                  </Badge>
                ) : (
                  <Badge variant="default" size="sm">
                    Inheriting default
                  </Badge>
                )}
              </div>
              <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                {theme.is_base
                  ? 'These are the platform defaults every broker inherits until they set their own.'
                  : 'Colours, type, spacing and motion for every page of your public site. Anything you leave blank keeps the built-in default.'}
              </p>
            </div>
            {!theme.is_base && theme.is_customized && editable && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<ArrowUturnLeftIcon className="h-4 w-4" />}
                disabled={reset.isPending}
                onClick={() => {
                  if (window.confirm('Reset the theme to the platform default?')) {
                    setIsDirty(false);
                    reset.mutate();
                  }
                }}
              >
                {reset.isPending ? 'Resetting…' : 'Reset'}
              </Button>
            )}
          </div>
        </Card>

        {Object.entries(theme.fields).map(([group, def]) => (
          <Card key={group} className="p-4">
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">{def.label}</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              {Object.entries(def.fields).map(([field, spec]) => (
                <ThemeField
                  key={field}
                  group={group}
                  field={field}
                  spec={spec}
                  value={draft[group]?.[field]}
                  editable={editable}
                  onChange={(value) => setToken(group, field, value)}
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      <PreviewPane
        html={preview.data?.html}
        device={device}
        onDeviceChange={setDevice}
        isLoading={preview.isLoading}
        isFetching={preview.isFetching}
        error={
          preview.error
            ? 'Could not render the preview with these settings. Fix any highlighted value and try again.'
            : null
        }
        onRetry={() => preview.refetch()}
      />

      {isDirty && editable && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-end gap-3 border-t border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <span className="text-sm text-gray-500 dark:text-gray-400">Unsaved theme changes</span>
          <Button
            variant="secondary"
            onClick={() => {
              setDraft(theme.tokens ?? {});
              setIsDirty(false);
            }}
          >
            Discard
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => save.mutate(draft, { onSuccess: () => setIsDirty(false) })}
          >
            {save.isPending ? 'Saving…' : 'Save theme'}
          </Button>
        </div>
      )}
    </div>
  );
}

function ThemeField({
  group,
  field,
  spec,
  value,
  editable,
  onChange,
}: {
  group: string;
  field: string;
  spec: ThemeFieldSpec;
  value: unknown;
  editable: boolean;
  onChange: (value: unknown) => void;
}) {
  const label = humanize(field);
  const type = specType(spec);
  const choices = specChoices(spec);

  if (type === 'bool') {
    return (
      <label className="flex items-center gap-3 sm:col-span-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={!editable}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      </label>
    );
  }

  if (choices) {
    return (
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
          {label}
        </label>
        <select
          value={(value as string) ?? ''}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value || undefined)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">Default</option>
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {humanize(choice)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (type === 'color') {
    const hex = typeof value === 'string' ? value : '';
    return (
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={`${label} colour picker`}
            value={/^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#ffffff'}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-10 flex-shrink-0 cursor-pointer rounded border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
          />
          <input
            type="text"
            aria-label={label}
            value={hex}
            placeholder="Default"
            disabled={!editable}
            onChange={(e) => onChange(e.target.value.trim() || undefined)}
            className={cn(
              'w-full rounded-lg border px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 dark:bg-gray-700 dark:text-white',
              hex && !/^#[0-9a-fA-F]{3,8}$/.test(hex)
                ? 'border-red-400'
                : 'border-gray-300 dark:border-gray-600',
            )}
          />
          {hex && (
            <button
              type="button"
              title="Clear"
              aria-label={`Clear ${label}`}
              onClick={() => onChange(undefined)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === 'int') {
    return (
      <Input
        label={label}
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        disabled={!editable}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    );
  }

  return (
    <Input
      label={label}
      value={(value as string) ?? ''}
      placeholder={LENGTH_PLACEHOLDERS[field] ?? 'Default'}
      disabled={!editable}
      onChange={(e) => onChange(e.target.value.trim() || undefined)}
      helperText={type === 'css_length' ? `e.g. ${LENGTH_PLACEHOLDERS[field] ?? '24px'}` : undefined}
      name={`${group}-${field}`}
    />
  );
}
